import type { TFunction } from "i18next";
import { DateTime } from "luxon";

import type {
  ContentItemOutline,
  ContributionKind,
  ItemProgress,
  ResourceDetails,
} from "@/redux/certifications/types";
import type {
  CommunityActivityItem,
  ContributionActivity,
  ContributionStatus,
  CountProgress,
  KnowledgeCheckState,
  ModuleOverview,
} from "@/domains/certifications/types";
import { needsEdit } from "@/domains/certifications/contributions";
import {
  getLiveSessionDetails,
  getResourceDetails,
} from "@/domains/certifications/utils";
import type {
  FileBadgeTone,
  RecordingRow,
  RequirementRow,
  RequirementRowKey,
  ResourceRow,
} from "@/features/certifications/module/types";
import { REQUIREMENT_ROW_KEYS } from "@/features/certifications/module/constants";
import {
  CONTENT_ITEM_TYPES,
  CONTENT_LOCK_REASONS,
  CONTRIBUTION_KINDS,
} from "@/domains/certifications/constants";

const UNDER_REVIEW_PERCENT = 18;
const WARNING_STATES: KnowledgeCheckState[] = [
  "underReview",
  "anotherRound",
  "failed",
];

const toPercent = (current: number, target: number) =>
  target > 0 ? Math.min(current / target, 1) * 100 : 100;

const cappedSum = (counts: CountProgress[]) =>
  counts.reduce(
    (sum, count) => ({
      current: sum.current + Math.min(count.current, count.target),
      target: sum.target + count.target,
    }),
    { current: 0, target: 0 },
  );

const knowledgeCheckRow = (
  t: TFunction,
  check: NonNullable<ModuleOverview["knowledgeCheck"]>,
): RequirementRow => {
  const percent: Record<KnowledgeCheckState, number> = {
    notStarted: 0,
    underReview: UNDER_REVIEW_PERCENT,
    anotherRound: 0,
    passed: 100,
    failed: 0,
  };
  return {
    key: REQUIREMENT_ROW_KEYS.KNOWLEDGE_CHECK,
    label: t("certification.module.rail.knowledgeCheck"),
    isComplete: check.isPassed,
    value: check.isPassed
      ? t("certification.module.rail.complete")
      : t(`certification.module.check.states.${check.checkState}`),
    percent: percent[check.checkState],
    isWarning: WARNING_STATES.includes(check.checkState),
  };
};

const communityRow = (
  t: TFunction,
  posts: CountProgress | null,
  comments: CountProgress | null,
  editNeeded: ContributionKind | null,
): RequirementRow => {
  const parts = [
    posts &&
      t("certification.module.community.postsProgress", {
        current: posts.current,
        count: posts.target,
      }),
    comments &&
      t("certification.module.community.commentsProgress", {
        current: comments.current,
        count: comments.target,
      }),
  ].filter(Boolean);
  const total = cappedSum(
    [posts, comments].filter((count): count is CountProgress => count !== null),
  );

  const isComplete = total.current >= total.target;
  const isEditNeeded = editNeeded !== null && !isComplete;

  return {
    key: REQUIREMENT_ROW_KEYS.COMMUNITY,
    label: t("certification.module.rail.community"),
    value: isEditNeeded
      ? t(`certification.module.rail.editNeeded.${editNeeded}`)
      : isComplete
        ? t("certification.module.rail.complete")
        : parts.join(" · "),
    percent: toPercent(total.current, total.target),
    isComplete,
    isWarning: isEditNeeded,
  };
};

const isCountMet = (count: CountProgress | null) =>
  !count || count.current >= count.target;

export const areOnlyVideosOutstanding = (week: ModuleOverview) =>
  !isCountMet(week.videos) &&
  isCountMet(week.readings) &&
  (!week.knowledgeCheck || week.knowledgeCheck.isPassed) &&
  isCountMet(week.posts) &&
  isCountMet(week.comments);

const itemCountRow = (
  t: TFunction,
  key: Extract<RequirementRowKey, "videos" | "readings">,
  count: CountProgress,
): RequirementRow => {
  const isComplete = isCountMet(count);
  return {
    key,
    label: t(`certification.module.rail.${key}`),
    value: isComplete
      ? t("certification.module.rail.complete")
      : t("certification.module.rail.count", {
          current: count.current,
          target: count.target,
        }),
    percent: toPercent(count.current, count.target),
    isComplete,
  };
};

export const buildRequirementRows = (
  t: TFunction,
  week: ModuleOverview,
  editNeeded: ContributionKind | null = null,
): RequirementRow[] => {
  const rows: RequirementRow[] = [];

  if (week.videos) {
    rows.push(itemCountRow(t, REQUIREMENT_ROW_KEYS.VIDEOS, week.videos));
  }
  if (week.readings) {
    rows.push(itemCountRow(t, REQUIREMENT_ROW_KEYS.READINGS, week.readings));
  }
  if (week.knowledgeCheck) {
    rows.push(knowledgeCheckRow(t, week.knowledgeCheck));
  }
  if (week.posts || week.comments) {
    rows.push(communityRow(t, week.posts, week.comments, editNeeded));
  }

  return rows;
};

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * 1024;

const FILE_TONES: Record<string, FileBadgeTone> = {
  PDF: "pdf",
  PPT: "slides",
  PPTX: "slides",
  KEY: "slides",
  XLS: "sheet",
  XLSX: "sheet",
  CSV: "sheet",
  DOC: "document",
  DOCX: "document",
  TXT: "document",
};

const fileFormat = (resource: ResourceDetails) => {
  const extension = resource.filename.includes(".")
    ? resource.filename.split(".").pop()
    : "";
  return (
    extension ||
    (resource.contentType.split("/").pop() ?? "")
  ).toUpperCase();
};

const formatFileSize = (t: TFunction, size: number) => {
  if (size >= BYTES_PER_MB) {
    return t("certification.module.resources.sizeMb", {
      value: (size / BYTES_PER_MB).toFixed(1),
    });
  }
  if (size >= BYTES_PER_KB) {
    return t("certification.module.resources.sizeKb", {
      value: Math.round(size / BYTES_PER_KB),
    });
  }
  return t("certification.module.resources.sizeBytes", { value: size });
};

export const buildResourceRows = (
  t: TFunction,
  items: ContentItemOutline[],
  itemProgress: ItemProgress[] = [],
): ResourceRow[] => {
  const lockedIds = new Set(
    itemProgress
      .filter((item) => item.lockReason === CONTENT_LOCK_REASONS.TRACK_LOCKED)
      .map((item) => item.id),
  );
  return [...items]
    .sort((a, b) => a.order - b.order)
    .flatMap((item) => {
      const resource = getResourceDetails(item);
      if (!resource || lockedIds.has(item.id)) {
        return [];
      }
      const format = fileFormat(resource);
      return [
        {
          id: item.id,
          title: item.title,
          format,
          tone: FILE_TONES[format] ?? "other",
          meta: [
            format,
            resource.size === null ? null : formatFileSize(t, resource.size),
          ]
            .filter(Boolean)
            .join(" · "),
        },
      ];
    });
};

export const buildRecordingRows = (
  items: ContentItemOutline[],
  allItems: ContentItemOutline[],
  now: DateTime = DateTime.now(),
): RecordingRow[] =>
  items
    .flatMap((item) => {
      const session = getLiveSessionDetails(item);
      if (!session || DateTime.fromISO(session.scheduledEnd) > now) {
        return [];
      }
      return [
        {
          id: item.id,
          title: item.title,
          recordedAt: session.scheduledStart,
          minutes: Math.round(session.durationSeconds / 60),
          video:
            session.recordingContentItem === null
              ? null
              : (allItems.find(
                  (candidate) =>
                    candidate.id === session.recordingContentItem &&
                    candidate.type === CONTENT_ITEM_TYPES.VIDEO,
                ) ?? null),
        },
      ];
    })
    .sort((a, b) => a.recordedAt.localeCompare(b.recordedAt));

export const topicPath = (topicTitle: string) =>
  `/community/topics/${encodeURIComponent(topicTitle)}`;

export const discussionPath = (discussionId: number) =>
  `/discussions/${discussionId}`;

export const editContributionPath = (
  item: Pick<CommunityActivityItem, "kind" | "id" | "discussionId">,
) =>
  item.kind === CONTRIBUTION_KINDS.POST
    ? `/discussions/form/${item.id}`
    : discussionPath(item.discussionId);

const WAITING_STATUSES: ContributionStatus[] = [
  "underReview",
  "countingPending",
];

const nudgeParts = (
  t: TFunction,
  kind: ContributionKind,
  count: CountProgress | null,
  contributions: ContributionActivity[],
) => {
  if (!count) {
    return [];
  }
  const own = contributions.filter((item) => item.kind === kind);
  const waiting = own.filter(({ status }) =>
    WAITING_STATUSES.includes(status),
  ).length;
  const toEdit = own.filter(({ status }) => needsEdit(status)).length;
  const missing = Math.max(count.target - count.current - waiting, 0);
  const edits = Math.min(toEdit, missing);
  const fresh = missing - edits;
  return [
    edits > 0 && t(`certification.module.community.nudge.edit.${kind}`),
    fresh > 0 &&
      t(`certification.module.community.nudge.add.${kind}`, { count: fresh }),
  ].filter((part): part is string => Boolean(part));
};

export const getCommunityNudge = (
  t: TFunction,
  posts: CountProgress | null,
  comments: CountProgress | null,
  contributions: ContributionActivity[],
) => {
  if (isCountMet(posts) && isCountMet(comments)) {
    return t("certification.module.community.nudge.done");
  }
  const parts = [
    ...nudgeParts(t, CONTRIBUTION_KINDS.POST, posts, contributions),
    ...nudgeParts(t, CONTRIBUTION_KINDS.COMMENT, comments, contributions),
  ];
  if (parts.length === 0) {
    return t("certification.module.community.nudge.waiting");
  }
  return t("certification.module.community.nudge.needed", {
    items: new Intl.ListFormat("en", { type: "conjunction" }).format(parts),
  });
};
